import React from 'react'
import { makeStyles } from '@mui/styles'
import { SwipeableDrawer } from '@mui/material'
import ThemeInterface from '@/theme-interface'
import lang from '@/lang'
import { brandHeading, brandText } from '@/utils/brand-typography'
import { ButtonSquare } from '@/modules/base/components/buttons'
import { brandPrimaryButton } from '@/modules/base/components/modal/upgrade-modal-v2/_helpers/brandButtons'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

// minimum touch target
const TAP_SIZE = 44

interface AvvInfoSheetProps {
  open: boolean
  onOpen: () => void
  onClose: () => void
  dataQsId: string
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  // the registration itself is a dialog - a drawer's default z-index would open behind it
  // (doubled class, so it wins over the drawer's own emotion style regardless of the injection order)
  root: {
    '&.MuiDrawer-root': {
      zIndex: theme.zIndex.modal,
    },
  },
  paper: {
    padding: '12px 24px calc(24px + env(safe-area-inset-bottom))',
    borderRadius: `${theme.borderRadiusExtraExtraLarge} ${theme.borderRadiusExtraExtraLarge} 0 0`,
    color: theme.palette.brand.text.primary,
    fontFamily: theme.brandFontFamilies.body,
  },
  handle: {
    alignSelf: 'center',
    width: 36,
    height: 4,
    marginBottom: 8,
    borderRadius: theme.borderRadiusPill,
    backgroundColor: theme.palette.brand.divider,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  title: {
    margin: 0,
    color: theme.palette.brand.text.heading,
    ...brandHeading(theme, 'h6'),
  },
  close: {
    flexShrink: 0,
    width: TAP_SIZE,
    height: TAP_SIZE,
    padding: 0,
    borderRadius: theme.borderRadiusPill,
    background: theme.backgroundColor,
    color: theme.palette.brand.text.heading,
    '&:hover': {
      background: theme.palette.brand.light,
      color: theme.palette.brand.text.heading,
    },
  },
  text: {
    margin: '0 0 12px',
    color: theme.palette.brand.text.primary,
    ...brandText(theme, 'regular'),
  },
  confirm: {
    height: 48,
    marginTop: 12,
    ...brandPrimaryButton(theme),
  },
}))

/**
 * Mobile: "Was ist der AVV?" as a bottom sheet - closes with the X, "Verstanden", a swipe down or a tap on the backdrop.
 * Desktop keeps the tooltip.
 */
const AvvInfoSheet = ({ open, onOpen, onClose, dataQsId }: AvvInfoSheetProps) => {
  const classes = useStyles()
  const texts = lang.registration.avvInfo

  return (
    <SwipeableDrawer
      anchor='bottom'
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      disableSwipeToOpen
      className={classes.root}
      classes={{ paper: classes.paper }}
      aria-labelledby={`${dataQsId}-title`}
      data-qs-id={dataQsId}>
      <span className={classes.handle} aria-hidden='true' />
      <div className={classes.header}>
        <h3 id={`${dataQsId}-title`} className={classes.title}>{texts.title}</h3>
        <ButtonSquare className={classes.close} aria-label={lang.registration.close} dataQsId={`${dataQsId}-close`} onClick={onClose}>
          <CloseRoundedIcon />
        </ButtonSquare>
      </div>
      <p className={classes.text}>{texts.text}</p>
      <p className={classes.text}>{texts.hint}</p>
      <ButtonSquare className={classes.confirm} label={texts.confirm} dataQsId={`${dataQsId}-confirm`} onClick={onClose} />
    </SwipeableDrawer>
  )
}

export default AvvInfoSheet
